import { useQuery } from "@tanstack/vue-query";
import { useI18n } from "vue-i18n";
import { computed, unref, type MaybeRef } from "vue";
import { api } from "@/helpers/api";
import { useUserStore } from "@/store/user";
import brandconfig, { isB2C } from "@/brandconfig";
import {
  createApiQueryByIdFn,
  createListQueryFn,
  EventsApi,
  useApiQuery,
  type APIQueryOptions,
  type EventsportalcacheGetRequest,
} from "@xs2event/js-lib/api";
import type { QueryClient } from "@tanstack/vue-query";

const TODAY = new Date().toISOString().substr(0, 10);
const eventsApi = new EventsApi();

export const usePopularEvents = () => {
  const { locale } = useI18n();

  return useQuery({
    queryKey: ["popular-events", locale],
    queryFn: async () => {
      const params = new URLSearchParams(brandconfig.quicklist.params || {});
      params.append("date_stop", "ge:" + TODAY);
      params.append("tickets_available", "ge:1");
      params.append("ticket_price_eur", "ge:1");
      params.append("popular_events", true);
      params.append("event_status", "in:[nosale,notstarted,postponed]");
      //params.append('sport_type', 'motorsport')
      params.append("page_size", 5);
      const response = await api.get("/events?" + params);
      // console.log(response.data.events)
      return response.data.events;
    },
    staleTime: 1000 * 60 * 60, // 1 hour
  });
};

export const useEvents = createListQueryFn(eventsApi, "eventsGet");

const PORTAL_EVENTS_CACHE_METHOD = "eventsportalcacheGet";

/**
 * Long enough that one fetch covers a typical browsing session, so the prefetch below actually pays
 * off. Lives here rather than at the call sites: the prefetched entry is only useful while the page
 * still considers it fresh, so the two must not drift apart.
 */
export const PORTAL_EVENTS_CACHE_STALE_TIME = 1000 * 60 * 30; // 30 minutes

/**
 * Every upcoming event (date_start >= today, any status except closed), priced for the calling
 * distributor. Takes no filters and no pagination — the portal filters and sorts client side.
 */
export const usePortalEventsCache = (payload?: MaybeRef<EventsportalcacheGetRequest>, options?: APIQueryOptions) => {
  const userStore = useUserStore();
  return useApiQuery(eventsApi, PORTAL_EVENTS_CACHE_METHOD, payload, {
    debounce: false,
    staleTime: PORTAL_EVENTS_CACHE_STALE_TIME,
    ...options,
    // The endpoint needs a session (it prices for the calling distributor), and the header that
    // reads it also renders on the login page. Issue #2941.
    enabled: computed(() => !!userStore.user && (unref(options?.enabled) ?? true)),
  });
};

/**
 * B2B only: ids of tournaments with at least one event in the portal events cache (tickets, or
 * soldout/nosale) — the same data the events listing renders, so a tournament missing here would
 * open on an empty page and tournament lists hide it. Same payload as the listing and the login
 * prefetch, so it reuses their cache entry. `null` on B2C, or until loaded — callers then hide
 * nothing. Issue #2898.
 */
export const useTournamentIdsWithEvents = () => {
  const { locale } = useI18n();
  const { data } = usePortalEventsCache(
    computed(() => ({ acceptLanguage: locale.value })),
    { enabled: !isB2C }
  );
  return computed(() => (isB2C || !data.value ? null : new Set(data.value.events.map((e) => e.tournamentId))));
};

/**
 * Warm the portal events cache from outside a component, so the events listing renders from cache
 * instead of waiting on this (large, unpaginated) response at click time.
 *
 * The query key mirrors the `[method, params]` shape `useApiQuery` builds — keep the two in sync, or
 * the prefetch and `usePortalEventsCache` end up writing to two separate cache entries.
 */
export const prefetchPortalEventsCache = (queryClient: QueryClient, payload: EventsportalcacheGetRequest) =>
  queryClient.prefetchQuery({
    queryKey: [PORTAL_EVENTS_CACHE_METHOD, payload],
    queryFn: () => eventsApi.eventsportalcacheGet(payload),
    staleTime: PORTAL_EVENTS_CACHE_STALE_TIME,
  });

export const useGetEventsByIds = createApiQueryByIdFn(eventsApi, "eventsGet", "eventId");

/** The portal events cache for the current locale: the same entry the login prefetch warms. */
export const useLocalePortalEventsCache = (options?: APIQueryOptions) => {
  const { locale } = useI18n();
  return usePortalEventsCache(
    computed(() => ({ acceptLanguage: locale.value })),
    options
  );
};

/**
 * Events by id, read from the portal events cache wherever it has them. Only the ids it lacks go to
 * `/events` -- mostly past events, since the cache holds nothing that started before today. That
 * waits for the cache, or every id would count as missing.
 *
 * `data` follows the order of `ids`; `isFetched` is true once every id has been looked up.
 */
export const useCachedEventsByIds = (ids: MaybeRef<string[]>, enabled: MaybeRef<boolean> = true) => {
  const cacheQuery = useLocalePortalEventsCache({ enabled });
  const cachedById = computed(
    () => new Map((cacheQuery.data.value?.events ?? []).map((event) => [event.eventId, event]))
  );
  const cacheDone = computed(() => cacheQuery.isFetched.value);

  const missingIds = computed(() => (cacheDone.value ? unref(ids).filter((id) => !cachedById.value.has(id)) : []));
  const fallbackQuery = useGetEventsByIds(missingIds, undefined, { enabled, debounce: false });

  const data = computed(() => {
    const fetchedById = new Map(fallbackQuery.data.value.map((event) => [event.eventId, event]));
    return unref(ids).flatMap((id) => {
      const event = cachedById.value.get(id) ?? fetchedById.get(id);
      return event ? [event] : [];
    });
  });

  // A disabled fallback -- nothing missing -- never reports fetched, so it only counts when it runs.
  const isFetched = computed(() => cacheDone.value && (!missingIds.value.length || fallbackQuery.isFetched.value));

  return { data, isFetched };
};
